import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Col, Form, Input, Row, Space, Table, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { recalcPaymentScheduleAmounts } from "../../utils/documents/recalc-payment-schedule-amounts.ts"
import { budgetFormat } from "../../utils/number/budget-format.ts"
import {
  validateNumber,
  validatePositiveNumber
} from "../../utils/validation/number.ts"
import InputNumber from "../Input/InputNumber.tsx"
import { DocumentCard } from "./DocumentCard.tsx"
import type {
  DocumentFormData,
  DocumentFormPaymentScheduleItem
} from "./document.types.ts"

interface DocumentCostSummarySectionProps {
  form: FormInstance<DocumentFormData>
}

function recalcTotals(form: FormInstance<DocumentFormData>) {
  const key: keyof DocumentFormData = "cost_summary"
  const values = form.getFieldValue(key)
  const lineItems = values?.line_items || []
  const discountAmount = values?.discount_amount || 0

  const subtotal = lineItems.reduce((sum: number, item: any) => {
    return sum + (+item?.amount || 0)
  }, 0)

  const total = subtotal - discountAmount

  const schedule: DocumentFormPaymentScheduleItem[] =
    form.getFieldValue("payment_schedule") || []
  const syncedSchedule = recalcPaymentScheduleAmounts(schedule, total)

  form.setFieldsValue({
    [key]: {
      ...values,
      subtotal,
      total
    },
    ...(syncedSchedule ? { payment_schedule: syncedSchedule } : {})
  })
}

export function DocumentCostSummarySection({
  form
}: DocumentCostSummarySectionProps) {
  const key: keyof DocumentFormData = "cost_summary"

  const subtotal = Form.useWatch([key, "subtotal"], form) || 0
  const total = Form.useWatch([key, "total"], form) || 0

  return (
    <DocumentCard title="Project Cost Summary" name={key}>
      {/* register in the form */}
      <Form.Item<DocumentFormData> name={[key, "subtotal"]} hidden>
        <input />
      </Form.Item>
      <Form.Item<DocumentFormData> name={[key, "total"]} hidden>
        <input />
      </Form.Item>

      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Line Items Table */}
        <Form.List name={[key, "line_items"]}>
          {(fields, { add, remove }) => (
            <Table
              pagination={false}
              dataSource={fields}
              scroll={{ x: "max-content" }}
              rowKey="key"
              bordered
              columns={[
                {
                  title: "Item",
                  width: "20%",
                  dataIndex: "name",
                  key: "item",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "item"]}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Required"
                        }
                      ]}
                    >
                      <Input placeholder="Enter name" />
                    </Form.Item>
                  )
                },
                {
                  title: "Description",
                  dataIndex: "name",
                  key: "description",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "description"]}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: "Required"
                        }
                      ]}
                    >
                      <Input placeholder="Enter description" />
                    </Form.Item>
                  )
                },
                {
                  title: "Amount",
                  width: "15%",
                  dataIndex: "name",
                  key: "amount",
                  align: "right",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "amount"]}
                      style={{ margin: 0 }}
                      rules={[
                        { required: true, message: "Required" },
                        { validator: validatePositiveNumber }
                      ]}
                    >
                      <InputNumber
                        stringMode
                        style={{ width: "100%" }}
                        placeholder="0"
                        suffix={"$"}
                        min={0}
                        max={99999999.99}
                        precision={2}
                        onChange={() => recalcTotals(form)}
                      />
                    </Form.Item>
                  )
                },
                {
                  title: "",
                  width: 50,
                  render: (_, field) => (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(field.name)
                        // setTimeout щоб form values оновились після remove
                        setTimeout(() => recalcTotals(form), 0)
                      }}
                    />
                  )
                }
              ]}
              footer={() => (
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Cost Item
                </Button>
              )}
            />
          )}
        </Form.List>

        {/* Cost Summary Section */}
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "8px"
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Subtotal */}
            <Row justify="space-between" align="middle">
              <Col>
                <Typography.Text strong>Subtotal</Typography.Text>
              </Col>
              <Col>
                <Typography.Text strong>
                  {budgetFormat(subtotal)}
                </Typography.Text>
              </Col>
            </Row>

            {/* Discount */}
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Form.Item
                  name={[key, "discount_description"]}
                  style={{ margin: 0 }}
                >
                  <Input placeholder="Discount description (optional)" />
                </Form.Item>
              </Col>
              <Col style={{ minWidth: "120px" }}>
                <Form.Item
                  name={[key, "discount_amount"]}
                  style={{ margin: 0 }}
                  rules={[{ validator: validateNumber }]}
                >
                  <InputNumber
                    className={"[&_input]:text-red-500"}
                    style={{ width: "100%" }}
                    max={subtotal}
                    placeholder="0"
                    suffix={"$"}
                    min={0}
                    precision={2}
                    onChange={() => recalcTotals(form)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Total */}
            <Row
              justify="space-between"
              align="middle"
              style={{ borderTop: "1px solid #d9d9d9", paddingTop: "12px" }}
            >
              <Col>
                <Typography.Text strong style={{ fontSize: "16px" }}>
                  Total
                </Typography.Text>
              </Col>
              <Col>
                <Typography.Text strong style={{ fontSize: "16px" }}>
                  {budgetFormat(total)}
                </Typography.Text>
              </Col>
            </Row>
          </Space>
        </div>
      </Space>
    </DocumentCard>
  )
}
